import React, { forwardRef } from 'react';
import type { Highlight } from '../../types';

interface HighlightBookmarkProps {
  highlight: Highlight;
  isActive: boolean;
  id: string;
  panelId: string;
  tabIndex: number;
  onSelect: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
}

/**
 * Single paper bookmark in the right-margin column. Renders as a tab in the
 * deck's tablist. Color is driven by `data-kind` (see HighlightBookmark.css).
 *
 * The three text spans (month / kind / nick) are intentionally
 * `white-space: nowrap` — editorial discipline keeps them short enough not
 * to overflow the notched clip-path. See HANDOFF §8.1.
 */
const HighlightBookmark = forwardRef<HTMLButtonElement, HighlightBookmarkProps>(
  ({ highlight, isActive, id, panelId, tabIndex, onSelect, onKeyDown }, ref) => (
    <button
      ref={ref}
      type="button"
      id={id}
      role="tab"
      aria-selected={isActive}
      aria-controls={panelId}
      tabIndex={tabIndex}
      data-kind={highlight.kind}
      className={`psl-hl-bm${isActive ? ' is-active' : ''}`}
      onClick={onSelect}
      onKeyDown={onKeyDown}
    >
      <span className="psl-hl-bm__dot" aria-hidden="true" />
      <span className="psl-hl-bm__month">{highlight.bookmarkDate}</span>
      <span className="psl-hl-bm__kind">{highlight.bookmarkKind}</span>
      <span className="psl-hl-bm__nick">{highlight.bookmarkNick}</span>
    </button>
  ),
);

HighlightBookmark.displayName = 'HighlightBookmark';

export default HighlightBookmark;
